import { useEffect, useRef } from 'react'

import { useAppDispatch, useAppSelector } from 'store/hooks'
import { closeSnackbar } from 'store/modules/snackbar/actions'
import { getSnackbarMessageSelector, getSnackbarVisibilitySelector } from 'store/modules/snackbar/selectors'

export const useAutoCloseSnackbarByTime = (
  snackbarForClosingMessages: string[],
  duration: number = 10000,
  enabled: boolean = true,
) => {
  const dispatch = useAppDispatch()
  const isSnackbarVisible = useAppSelector(getSnackbarVisibilitySelector)
  const currentSnackbarMessage = useAppSelector(getSnackbarMessageSelector)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastSnackbarKeyRef = useRef<string | null>(null)

  const isCurrentSnackbarShouldBeClosed =
    typeof currentSnackbarMessage === 'string'
      ? snackbarForClosingMessages.includes(currentSnackbarMessage)
      : snackbarForClosingMessages.includes(currentSnackbarMessage?.toString() ?? '')

  const snackbarKey = `${Date.now()}-${currentSnackbarMessage}`

  useEffect(() => {
    if (!isSnackbarVisible || !isCurrentSnackbarShouldBeClosed || !enabled) {
      return
    }

    if (lastSnackbarKeyRef.current !== snackbarKey) {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }

      timerRef.current = setTimeout(() => {
        dispatch(closeSnackbar())
        timerRef.current = null
        lastSnackbarKeyRef.current = null
      }, duration)

      lastSnackbarKeyRef.current = snackbarKey
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
        lastSnackbarKeyRef.current = null
      }
    }
  }, [
    isSnackbarVisible,
    currentSnackbarMessage,
    isCurrentSnackbarShouldBeClosed,
    duration,
    dispatch,
    snackbarKey,
    enabled,
  ])
}
